import {Button, ButtonGroup, Card, Chip, Icon, Label, Select, TextField} from "@nike/eds";
import {edsSpace16, edsSpace32} from "@nike/eds/dist/tokens";
import React, {useState} from "react";
import {Reason} from "./Type";
import ManualClearingConfirmModal from "./ManualClearingConfirmModal";
import {ManualClearingService} from "./ManualClearingService";
import "./ManualClearing.css";

interface ReasonOption {
    value: string;
    label: string;
}

const reasonOptions: ReasonOption[] = [
    {value: "SR", label: Reason.SR},
    {value: "EC", label: Reason.EC},
    {value: "ZDT", label: Reason.ZDT},
];


const SEPARATORS = /[\r\n\t;,]+/;
const splitIntoIds = (raw: string): string[] =>
    raw.split(SEPARATORS).map(id => id.trim()).filter(id => id !== "");

const mergeUniqueIds = (existing: string[], additions: string[]): string[] => {
    const next = [...existing];
    additions.forEach(id => {
        if (!next.includes(id)) {
            next.push(id);
        }
    });
    return next;
};

interface ManualClearingProps {
    manualClearingService: ManualClearingService;
}

export function ManualClearing({manualClearingService}: ManualClearingProps) {

    const [documentIds, setDocumentIds] = useState<string[]>([]);
    const [currentInput, setCurrentInput] = useState("");
    const [documentIdsHasErrors, setDocumentIdsHasErrors] = useState(false);
    const [reason, setReason] = useState<ReasonOption | null>(null);
    const [reasonHasErrors, setReasonHasErrors] = useState(false);
    const [comment, setComment] = useState("");
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const formGroupStyle = {marginBottom: edsSpace16};
    const formGroupLastItemStyle = {marginBottom: edsSpace32};

    const resetForm = () => {
        setDocumentIds([]);
        setCurrentInput("");
        setDocumentIdsHasErrors(false);
        setReason(null);
        setReasonHasErrors(false);
        setComment("");
    };

    const commitInput = (input: string): string[] => {
        const additions = splitIntoIds(input);
        if (additions.length === 0) {
            return documentIds;
        }
        const mergedIds = mergeUniqueIds(documentIds, additions);
        setDocumentIds(mergedIds);
        setCurrentInput("");
        setDocumentIdsHasErrors(false);
        return mergedIds;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (SEPARATORS.test(value)) {
            commitInput(value);
            return;
        }
        setCurrentInput(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && currentInput === "" && documentIds.length > 0) {
            e.preventDefault();
            setDocumentIds(ids => ids.slice(0, -1));
            return;
        }
        if (e.key !== "Enter" && e.key !== "Tab") {
            return;
        }
        if (currentInput.trim() === "") {
            return;
        }
        e.preventDefault();
        commitInput(currentInput);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData("text");
        if (!SEPARATORS.test(pasted)) {
            return;
        }
        e.preventDefault();
        commitInput(currentInput + pasted);
    };

    const removeDocumentId = (index: number) => {
        setDocumentIds(IDs => IDs.filter((_, i) => i !== index));
    };

    const validateForm = () => {
        const finalIds = currentInput.trim() !== "" ? commitInput(currentInput) : documentIds;
        const idsInvalid = finalIds.length === 0;
        const reasonInvalid = reason === null;

        setDocumentIdsHasErrors(idsInvalid);
        setReasonHasErrors(reasonInvalid);

        if (idsInvalid || reasonInvalid) {
            return;
        }
        setModalIsOpen(true);
    };

    const closeModal = (isConfirmed: boolean) => {
        setModalIsOpen(false);
        if (isConfirmed) {
            resetForm();
        }
    };

    const chipContainerClassName = `manual-clearing-chip-container${documentIdsHasErrors ? " manual-clearing-chip-container--has-errors" : ""}`;

    return (
        <div className="eds-grid eds-elevation--4">
            <Card>
                <div className="form-group" style={formGroupStyle}>
                    <Label htmlFor="document-ids-input">Special Trade Program - Document Id(s)</Label>
                    <div className={chipContainerClassName} onClick={() => document.getElementById("document-ids-input")?.focus()}>
                        {documentIds.map((id, index) => (
                            <Chip
                                key={`${id}-${index}`}
                                size="small"
                                afterSlot={
                                    <span
                                        role="button"
                                        aria-label={`Remove ${id}`}
                                        className="manual-clearing-chip-remove"
                                        onClick={event => {
                                            event.stopPropagation();
                                            removeDocumentId(index);
                                        }}
                                    >
                                        <Icon name="Close" size="s"/>
                                    </span>
                                }
                            >
                                {id}
                            </Chip>
                        ))}
                        <input
                            id="document-ids-input"
                            type="text"
                            className="manual-clearing-chip-input"
                            value={currentInput}
                            placeholder={documentIds.length === 0 ? "Click to add the STP Document Ids" : ""}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            aria-invalid={documentIdsHasErrors}
                        />
                    </div>
                    {documentIdsHasErrors && (
                        <span className="manual-clearing-error">No Special Trade Program Document ID added</span>
                    )}
                    <small className="manual-clearing-helper">
                        Press <kbd>Enter</kbd> or <kbd>Tab</kbd> to add multiple Document IDs
                    </small>
                </div>

                <div className="form-group" style={formGroupStyle}>
                    <Label htmlFor="reason-select">
                        Reason
                    </Label>
                    <Select
                        id="reason-select"
                        label="Reason"
                        hideLabel
                        options={reasonOptions}
                        value={reason}
                        onChange={selected => {
                            const next = selected as ReasonOption | null;
                            setReason(next);
                            if (next !== null) {
                                setReasonHasErrors(false);
                            }
                        }}
                        placeholder="Select a reason"
                    />
                    {reasonHasErrors && (
                        <span className="manual-clearing-error">No reason selected</span>
                    )}
                </div>

                <div className="form-group form-group-last-item" style={formGroupLastItemStyle}>
                    <TextField
                        id="comment-text-field"
                        label="Comment"
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder="Click to add a comment ..."
                    />
                </div>

                <div className="form-group form-group-last-item" style={formGroupStyle}>
                    <ButtonGroup>
                        <Button variant="primary" type="submit" onClick={validateForm}>Trigger event</Button>
                    </ButtonGroup>
                </div>
            </Card>

            {modalIsOpen && (
                <ManualClearingConfirmModal
                    isOpen={modalIsOpen}
                    onClose={closeModal}
                    documentIds={documentIds}
                    reason={reason?.label ?? ""}
                    comment={comment}
                    manualClearingService={manualClearingService}
                />
            )}
        </div>
    );
}
